import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { radarTimelineQueryKey } from '@/hooks/useRadar';

export interface RadarStreamEvent {
  empresaId: number;
  tipo: string;
  pipelineId?: number;
  payload?: { novas?: number; alertas?: number; sessaoId?: number };
  em: string;
}

export interface UseRadarStreamOptions {
  /** Timeline aberta: o evento daquele pipeline também invalida a timeline. */
  pipelineId?: number;
  onRadar?: (evento: RadarStreamEvent) => void;
  habilitado?: boolean;
  /** Backoff de reconexão, em ms. Exposto para os testes. */
  backoffMs?: number[];
}

/** Backoff crescente: o servidor pode reiniciar e não queremos laço apertado. */
const BACKOFF_PADRAO = [1_000, 2_000, 4_000, 8_000, 15_000, 30_000];

const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

interface QuadroSse {
  evento: string | null;
  dados: string | null;
}

/** Fatia um quadro SSE (`event:`/`data:`), ignorando keep-alives (`:`). */
export function lerQuadroSse(quadro: string): QuadroSse {
  let evento: string | null = null;
  const dados: string[] = [];

  for (const linha of quadro.split('\n')) {
    const texto = linha.trimEnd();
    if (!texto || texto.startsWith(':')) continue;
    if (texto.startsWith('event:')) evento = texto.slice(6).trim();
    else if (texto.startsWith('data:')) dados.push(texto.slice(5).trim());
  }

  return { evento, dados: dados.length ? dados.join('\n') : null };
}

/**
 * Tempo real do Radar por SSE.
 *
 * Consumido com `fetch` + `ReadableStream` porque o endpoint é autenticado por
 * header `Authorization`: `EventSource` não permite cabeçalhos e exigiria o
 * token na URL, onde ele acabaria em log de proxy.
 *
 * Se o stream falhar, a tela não quebra: o `refetchInterval` do React Query
 * (ver `useRadarTimeline`/`useRadarResumo`) é o fallback oficial.
 */
export function useRadarStream({
  pipelineId,
  onRadar,
  habilitado = true,
  backoffMs = BACKOFF_PADRAO,
}: UseRadarStreamOptions = {}) {
  const { accessToken } = usePortalAuth();
  const queryClient = useQueryClient();
  const [conectado, setConectado] = useState(false);
  const onRadarRef = useRef(onRadar);
  onRadarRef.current = onRadar;

  useEffect(() => {
    if (!habilitado || !accessToken || typeof fetch !== 'function') return undefined;

    let cancelado = false;
    let tentativa = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();

    const tratar = (evento: RadarStreamEvent) => {
      if (evento?.tipo !== 'comunicacoes') return;
      void queryClient.invalidateQueries({ queryKey: ['radar', 'resumo'] });
      if (evento.pipelineId && (pipelineId === undefined || evento.pipelineId === pipelineId)) {
        void queryClient.invalidateQueries({ queryKey: radarTimelineQueryKey(evento.pipelineId) });
      }
      onRadarRef.current?.(evento);
    };

    const conectar = async () => {
      if (cancelado) return;
      try {
        const resposta = await fetch(`${apiBase}/portal/radar/stream`, {
          headers: { Authorization: `Bearer ${accessToken}`, Accept: 'text/event-stream' },
          signal: controller.signal,
        });
        if (!resposta.ok || !resposta.body) throw new Error('RADAR_STREAM_UNAVAILABLE');

        const reader = resposta.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!cancelado) {
          const pedaco = await reader.read();
          if (pedaco.done) break;
          buffer += decoder.decode(pedaco.value, { stream: true });

          const quadros = buffer.split('\n\n');
          buffer = quadros.pop() || '';

          for (const quadro of quadros) {
            const { evento, dados } = lerQuadroSse(quadro);
            if (evento === 'conectado') {
              tentativa = 0;
              if (!cancelado) setConectado(true);
              continue;
            }
            if (evento !== 'radar' || !dados) continue;
            try {
              tratar(JSON.parse(dados) as RadarStreamEvent);
            } catch {
              // Quadro inválido não derruba o stream.
            }
          }
        }
      } catch {
        // Nunca propaga: o polling do React Query mantém a tela correta.
      } finally {
        if (!cancelado) {
          setConectado(false);
          const espera = backoffMs[Math.min(tentativa, backoffMs.length - 1)];
          tentativa += 1;
          timer = setTimeout(() => void conectar(), espera);
        }
      }
    };

    void conectar();

    return () => {
      cancelado = true;
      if (timer) clearTimeout(timer);
      controller.abort();
    };
  }, [accessToken, habilitado, pipelineId, queryClient, backoffMs]);

  return { conectado };
}
